// VOY-571: user-facing messages for the order and item states the database actually stores.
// Order states come from orders_status_check and item states from order_items_status_check
// (supabase/migrations/20260618002500_baseline_schema.sql). The stripe-webhook records a booking it
// left for a person as status 'failed' plus an error_message flag (docs/runbooks/
// unbooked-refunds-review.md §2), so that combination gets its own review message instead of the
// plain failure. Only the 'paid' state may say the order is paid.

export type OrderStatus =
  | 'created'
  | 'holding'
  | 'holding_partial'
  | 'paid'
  | 'confirming'
  | 'confirmed'
  | 'failed'
  | 'cancelled'

export type OrderDisplayKey = OrderStatus | 'needs_review' | 'unknown'

export type ItemStatus = 'pending' | 'hold_created' | 'held' | 'confirmed' | 'failed' | 'cancelled'

export type StatusTone = 'pending' | 'success' | 'warning' | 'error' | 'neutral'

export interface OrderStatusMessage {
  title: string
  message: string
  tone: StatusTone
  /** Show the contact line: the customer may need to reach us about this order. */
  showContact: boolean
}

export const ORDER_STATUS_MESSAGES: Record<OrderDisplayKey, OrderStatusMessage> = {
  created: {
    title: 'Waiting for payment confirmation',
    message: "We haven't received confirmation of your payment yet. This page updates on its own.",
    tone: 'pending',
    showContact: false,
  },
  holding: {
    title: 'Reserving your booking',
    message: "We're holding your selection with the airline or hotel before confirming it. This page updates on its own.",
    tone: 'pending',
    showContact: false,
  },
  holding_partial: {
    title: 'Part of your booking is reserved',
    message: "We've reserved part of your selection and are still working on the rest. This page updates on its own.",
    tone: 'pending',
    showContact: false,
  },
  paid: {
    title: 'Payment received',
    message: 'Your order is paid. We are now confirming your booking with the airline or hotel. This page updates on its own.',
    tone: 'pending',
    showContact: false,
  },
  confirming: {
    title: 'Confirming your booking',
    message: "Your payment went through and we're confirming your booking with the airline or hotel. This page updates on its own.",
    tone: 'pending',
    showContact: false,
  },
  confirmed: {
    title: 'Booking confirmed',
    message: "You're all set. Your booking is confirmed with the airline or hotel.",
    tone: 'success',
    showContact: false,
  },
  failed: {
    title: "We couldn't complete your booking",
    message: "Your booking didn't go through. If you see a charge for anything that wasn't booked, contact us with your order ID.",
    tone: 'error',
    showContact: true,
  },
  needs_review: {
    title: 'Your booking needs a manual check',
    message: "We couldn't finish your booking automatically, so our team is checking it by hand. We'll contact you with the result. Please don't book again until you hear from us.",
    tone: 'warning',
    showContact: true,
  },
  cancelled: {
    title: 'Booking cancelled',
    message: 'This booking was cancelled. If you have questions about a charge, contact us with your order ID.',
    tone: 'neutral',
    showContact: true,
  },
  unknown: {
    title: "We can't show your booking status",
    message: "We couldn't load the status of this booking. Contact us with your order ID and we'll check it for you.",
    tone: 'warning',
    showContact: true,
  },
}

export const ITEM_STATUS_LABELS: Record<ItemStatus | 'unknown', string> = {
  pending: 'Waiting to be booked',
  hold_created: 'Being reserved',
  held: 'Reserved',
  confirmed: 'Booked',
  failed: 'Not booked',
  cancelled: 'Cancelled',
  unknown: 'Status unavailable',
}

/** States the order can still move out of, so the page keeps polling. */
export const IN_PROGRESS_KEYS: ReadonlySet<OrderDisplayKey> = new Set([
  'created',
  'holding',
  'holding_partial',
  'paid',
  'confirming',
])

const REVIEW_PREFIXES = ['partially_booked_needs_review_', 'partially_booked_unpaid_']

/** True when the stripe-webhook left this failed order for a person (runbook §2). */
export function isReviewFlag(errorMessage: string | null | undefined): boolean {
  if (!errorMessage) return false
  return errorMessage === 'hotel_check_unavailable' || REVIEW_PREFIXES.some((p) => errorMessage.startsWith(p))
}

const ORDER_STATUSES: ReadonlySet<string> = new Set<OrderStatus>([
  'created',
  'holding',
  'holding_partial',
  'paid',
  'confirming',
  'confirmed',
  'failed',
  'cancelled',
])

/** Maps the stored order status (and error_message) to the message the customer sees. */
export function resolveOrderDisplay(
  status: string | null | undefined,
  errorMessage: string | null | undefined,
): OrderDisplayKey {
  if (!status || !ORDER_STATUSES.has(status)) return 'unknown'
  if (status === 'failed' && isReviewFlag(errorMessage)) return 'needs_review'
  return status as OrderStatus
}

/**
 * Labels for a failed item whose money was settled (order_items.refund_status, VOY-618). Juan's
 * decision 2026-10-30: 'refunded' and 'released' say so; any other refund_status keeps the item
 * status label.
 */
export const FAILED_ITEM_REFUND_LABELS: Record<'refunded' | 'released', string> = {
  refunded: 'Refunded',
  released: 'Not charged',
}

export function itemStatusLabel(status: string | null | undefined, refundStatus?: string | null): string {
  if (status === 'failed' && (refundStatus === 'refunded' || refundStatus === 'released')) {
    return FAILED_ITEM_REFUND_LABELS[refundStatus]
  }
  if (status && status in ITEM_STATUS_LABELS) return ITEM_STATUS_LABELS[status as ItemStatus]
  return ITEM_STATUS_LABELS.unknown
}
